import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteContext } from "../context/SiteContext";
import { api } from "../services/api";
import "./Home.css";

import { 
  Scale, 
  Calendar, 
  Search, 
  Globe, 
  MessagesSquare,
  FileText,
  HelpCircle,
  Mail,
  ChevronDown,
  ChevronUp,
  CheckCircle2
} from "lucide-react";

// Função auxiliar para mapear o nome do ícone do banco para o componente real
const getIconComponent = (iconName) => {
  const props = { size: 32, strokeWidth: 1.5 };
  switch (iconName) {
    case "lei": return <Scale {...props} />;
    case "calendario": return <Calendar {...props} />;
    case "consulta": return <Search {...props} />;
    case "site_prefeitura": return <Globe {...props} />;
    case "mensagem": return <MessagesSquare {...props} />;
    case "processo": return <FileText {...props} />;
    case "documento": return <FileText {...props} />;
    case "site_percept": return <Globe {...props} />;
    case "interrogacao": return <HelpCircle {...props} />;
    case "envelope": return <Mail {...props} />;
    default: return <Scale {...props} />;
  }
};

export default function Home() {
  const { config, loading } = useContext(SiteContext);

  const [banners, setBanners] = useState([]);
  const [bannerIndex, setBannerIndex] = useState(0);

  const [newsData, setNewsData] = useState([]);
  const [activeNewsIndex, setActiveNewsIndex] = useState(0);

  // Estados das tabelas dinâmicas
  const [acessosRapidos, setAcessosRapidos] = useState([]);
  const [faqData, setFaqData] = useState([]);
  const [openFaqIndex, setOpenFaqIndex] = useState(null); 

  const mainNews = newsData[activeNewsIndex];
  const secondaryNews = newsData.filter((_, idx) => idx !== activeNewsIndex).slice(0, 3);

  useEffect(() => {
    async function loadData() {
      if (!config?.id) return;

      try {
        // 1. Busca Banners
        const resBanners = await api.get(`/banners?site=${config.id}`);
        setBanners(resBanners.data || []);

        // 2. Busca Notícias
        const resNews = await api.get(`/noticias?cliente_id=${config.id}`);
        const formattedNews = (resNews.data || [])
          .filter((n) => n.publicado)
          .slice(0, 4)
          .map((n) => ({
            id: n.id,
            title: n.titulo,
            date: new Date(n.created_at || n.createdAt).toLocaleDateString("pt-BR"),
            desc: n.resumo || "",
            imagem: n.imagem || "",
            conteudo: n.conteudo
          }));
        setNewsData(formattedNews);

        // 3. Busca Acesso Rápido
        try {
          const resAcesso = await api.get(`/acessos-rapidos/cliente/${config.id}`);
          if (resAcesso.data?.botoes_menu) {
            setAcessosRapidos(resAcesso.data.botoes_menu.filter((item) => item.ativo));
          }
        } catch (error) {
          if (error.response?.status !== 404) console.error("Erro no Acesso Rápido:", error);
        }

        // 4. Busca FAQ
        try {
          const resFaq = await api.get(`/faq/cliente/${config.id}`);
          if (resFaq.data?.perguntas_respostas) {
            const sortedFaq = resFaq.data.perguntas_respostas
              .filter((item) => item.ativo)
              .sort((a, b) => a.ordem - b.ordem);
            setFaqData(sortedFaq);
          }
        } catch (error) {
          if (error.response?.status !== 404) console.error("Erro no FAQ:", error);
        }

      } catch (err) {
        console.error("Erro ao carregar dados da Home:", err);
      }
    }

    loadData();
  }, [config]);

  // Autoplay do carrossel
  useEffect(() => {
    if (banners.length === 0) return;
    const interval = setInterval(() => {
      setBannerIndex((prev) => (prev + 1) % banners.length);
    }, 6000); 
    return () => clearInterval(interval);
  }, [banners]);

  function nextBanner() { setBannerIndex((prev) => (prev + 1) % banners.length); }
  function prevBanner() { setBannerIndex((prev) => (prev - 1 + banners.length) % banners.length); }
  function goToBanner(index) { setBannerIndex(index); }
  
  function toggleFaq(index) {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  }

  if (loading) {
    return (
      <div className="loader-container">
        <div className="hexagon-spinner"></div>
        <p className="loader-text">Carregando...</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="loader-container">
        <h2>Site não encontrado</h2>
      </div>
    );
  }

  const banner = banners[bannerIndex];

  return (
    <div className="home-page">
      {/* 1. HERO BANNER */}
      {banner && (
        <section
          className="hero-banner"
          style={{ backgroundImage: `url(${banner.imagem})` }}
        >
          <div className="hero-content container">
            <h1>{banner.titulo}</h1>
            <p>{banner.descricao}</p>
            {banner.botao_texto && (
              <a 
                href={banner.botao_link} 
                target={banner.botao_link?.startsWith("http") ? "_blank" : "_self"} 
                rel="noopener noreferrer"
              >
                <button className="btn-primary">{banner.botao_texto}</button>
              </a>
            )}
          </div>
          <button className="carousel-btn prev" onClick={prevBanner}>❮</button>
          <button className="carousel-btn next" onClick={nextBanner}>❯</button>
          <div className="carousel-indicators">
            {banners.map((_, index) => (
              <button
                key={index}
                className={`indicator-dot ${index === bannerIndex ? "active" : ""}`}
                onClick={() => goToBanner(index)}
              />
            ))}
          </div>
        </section>
      )}

      {/* 2. NOTÍCIAS */}
      {newsData.length > 0 && (
        <section className="news-section container">
          <div className="news-header">
            <h2 className="section-title">Notícias e Comunicados</h2>
            <Link to="/noticias" className="btn-view-all">
              Ver Todas as Notícias ➔
            </Link>
          </div>

          <div className="news-custom-grid">
            {mainNews && (
              <div className="main-news-card">
                <Link to={`/noticia/${mainNews.id}`} className="main-card-link-wrapper">
                  <div 
                    className="main-card-img" 
                    style={{ backgroundImage: `url(${mainNews.imagem || "/assets/placeholder-news.jpg"})` }}
                  />
                </Link>
                <div className="card-body">
                  <span className="date">{mainNews.date}</span>
                  <Link to={`/noticia/${mainNews.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                    <h3>{mainNews.title}</h3>
                  </Link>
                  <p>{mainNews.desc}</p>
                  
                  {/* Botão de Ler Notícia Completa conectado e funcional */}
                  <Link to={`/noticia/${mainNews.id}`} className="btn-outline-link">
                    <button className="btn-outline">Ler Notícia Completa ➔</button>
                  </Link>
                </div>
              </div>
            )}

            <div className="side-news-column">
              {secondaryNews.map((item) => {
                const originalIndex = newsData.findIndex((n) => n.id === item.id);
                return (
                  <div 
                    key={item.id} 
                    className="side-news-card"
                    onClick={() => setActiveNewsIndex(originalIndex)}
                  >
                    <div 
                      className="side-card-img" 
                      style={{ backgroundImage: `url(${item.imagem || "/assets/placeholder-news.jpg"})` }}
                    />
                    <div className="side-card-info">
                      <span className="date">{item.date}</span>
                      <h4>{item.title}</h4>
                      <p>{item.desc.substring(0, 55)}...</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 3. TRANSPARÊNCIA E ACESSO RÁPIDO */}
      {acessosRapidos.length > 0 && (
        <section className="transparency-section">
          <div className="container">
            <h2 className="section-title">Transparência e Acesso Rápido</h2>
            <div className="access-container-box">
              <div className="access-grid-mini">
                {acessosRapidos.map((item, index) => (
                  <a 
                    href={item.conteudo} 
                    target={item.tipo_acesso === "link" ? "_blank" : "_self"}
                    rel="noreferrer"
                    key={index} 
                    className="mini-access-item"
                  >
                    <div className="hover-fill"></div>
                    <div className="mini-access-content">
                      <div className="icon-wrapper">
                        {getIconComponent(item.icone)}
                      </div>
                      <span className="mini-access-text">{item.nome}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 4. PERGUNTAS FREQUENTES (FAQ) */}
      {faqData.length > 0 && (
        <section className="faq-section container">
          <h2 className="section-title text-center">Perguntas Frequentes</h2>
          <div className="faq-container">
            {faqData.map((item, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div className={`faq-item ${isOpen ? "open" : ""}`} key={item.id || index}>
                  <button className="faq-question" onClick={() => toggleFaq(index)}>
                    <span>{item.pergunta}</span>
                    {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  
                  <div 
                    className="faq-answer-wrapper" 
                    style={{ height: isOpen ? "auto" : "0px", overflow: "hidden" }}
                  >
                    <div className="faq-answer-content">
                      {item.respostas?.map((resp, i) => (
                        <div key={i} className="faq-answer-row">
                          <CheckCircle2 size={16} className="faq-answer-icon" />
                          <p>{resp}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 5. LOCALIZAÇÃO (IMAGEM DINÂMICA) */}
      <section className="location-section">
        <img 
          src={config?.imagem_fundo || "/assets/banners/banner1.jpg"} 
          alt="Fundo Localização" 
          className="bg-image-html" 
        />
        <div className="location-overlay">
          <div className="container map-wrapper">
            <h2 className="section-title white">Nossa Localização</h2>
            <div className="map-container">
              {config?.endereco_mapa ? (
                <iframe
                  title="Mapa da Instituição"
                  src={config.endereco_mapa}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                ></iframe>
              ) : (
                <div className="map-placeholder">
                  <p>Localização não configurada.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}